import { FileInput, MessagesSquare, PawPrint } from 'lucide-react';

import { useDashboardControllerResumo } from '@/api/generated/dashboard/dashboard';
import { ArtigosDestaqueLista } from '@/features/dashboard/components/ArtigosDestaqueLista';
import { CardAtalho } from '@/features/dashboard/components/CardAtalho';
import { ExamesRecentesTabela } from '@/features/dashboard/components/ExamesRecentesTabela';
import { StatsCard } from '@/features/dashboard/components/StatsCard';
import { EstadoErro } from '@/shared/components/estados';
import { ROTAS, rotaDeNovaRequisicao } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Topo: 4 cards — 2 atalhos de ação e 2 números.
 * Abaixo: exames recentes (linha inteira) e, embaixo, artigos.
 *
 * Empilhados, não lado a lado: a tabela de exames precisa da largura toda
 * pra mostrar as colunas sem apertar, e artigos é conteúdo secundário —
 * ganha mais espaço por linha (cards maiores) descendo pra própria seção.
 *
 * ⚠️ `examesConcluidos7Dias` vem do resumo mas NÃO é exibido: os 4 cards de
 * cima são 2 atalhos + 2 números, e um quinto quebraria o `lg:grid-cols-4`.
 * Fica no contrato para quando a faixa tiver um lugar na tela.
 */
export function DashboardPage() {
  const { data, isPending, isError, refetch } = useDashboardControllerResumo();
  const { pode } = usePermissoes();

  // Sem `requisicoes_exames.criar` o atalho levaria ao 403 da própria rota —
  // card apagado com o motivo é melhor que um clique que só falha lá dentro.
  const podeCriarRequisicao = pode('requisicoes_exames.criar');

  // Mesmo gate da entrada "Pacientes" da sidebar: sem `pacientes.visualizar` o
  // card vira só o número, sem link que levaria ao 403 da própria rota.
  const podeVerPacientes = pode('pacientes.visualizar');

  if (isPending) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  if (isError || !data) {
    return <EstadoErro aoTentarDeNovo={() => void refetch()} />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Clube primeiro: é conteúdo da marca Hormonalle, e por isso usa o
            gradiente e o ícone dela — não os da NOHRA Clinic. */}
        <CardAtalho
          imagemIcone="/icone-hormonalle.png"
          gradiente="bg-gradient-hormonalle"
          titulo="Clube Hormonalle"
          descricao="Seu nível de cliente e cupons de parceiros"
          para={ROTAS.clube}
        />
        <CardAtalho
          icone={FileInput}
          titulo="Solicitar novos exames"
          descricao="Criar nova solicitação de exame"
          motivoIndisponivel="Seu cargo não permite criar requisições"
          {...(podeCriarRequisicao ? { para: rotaDeNovaRequisicao() } : {})}
        />
        <StatsCard
          titulo="Pacientes"
          valor={data.totalPacientes}
          icone={PawPrint}
          cor="pink"
          {...(podeVerPacientes ? { para: ROTAS.pacientes } : {})}
        />
        <StatsCard
          titulo="Conexão com especialista"
          valor={data.totalDiscussoesTecnicas}
          icone={MessagesSquare}
          cor="blue"
          tendencia="discussões técnicas cadastradas"
          paraOIcone={ROTAS.discussoes}
        />
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Exames Recentes
          </h3>
        </div>
        <ExamesRecentesTabela exames={data.examesRecentes} />
      </div>

      <ArtigosDestaqueLista artigos={data.artigosDestaque} />
    </div>
  );
}
